import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { rememberSpace } from "@/lib/pwa";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const location = useLocation();
  const planParam = new URLSearchParams(location.search).get("plan");
  const target = planParam ? `/app/billing/start?plan=${planParam}` : (location.state?.from || "").startsWith("/app") ? location.state.from : "/app/dashboard";
  useEffect(() => { if (user) navigate(target, { replace: true }); }, [user, navigate, target]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email.trim(), password);
      toast.success("Connexion réussie");
      navigate(target, { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "EMPLOYEE_ACCOUNT") { rememberSpace("employee"); toast.info(d.message); navigate(`/salarie/connexion?email=${encodeURIComponent(email.trim())}`, { replace: true }); return; }
      setError(formatApiErrorDetail(d) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
        <Logo light />
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Reprenez le contrôle de votre visibilité.</h2>
          <p className="mt-4 text-slate-300 max-w-md">Un tableau de bord clair pour savoir où vous en êtes et quoi faire ensuite.</p>
        </div>
        <p className="relative text-sm text-slate-400">© 2026 Respondia — Pilotage & Performance</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-heading text-3xl font-bold">Connexion</h1>
          <p className="mt-2 text-slate-500">Accédez à votre espace Respondia.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5 h-11" placeholder="vous@exemple.fr" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="forgot-link">Oublié ?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5 h-11" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-xl">
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            Pas encore de compte ? <Link to={planParam ? `/register?plan=${planParam}` : "/register"} className="text-blue-600 font-medium hover:underline" data-testid="to-register-link">Créer un compte</Link>
          </p>
          <p className="mt-3 text-sm text-slate-500 text-center">
            Vous êtes salarié ? <Link to="/salarie/connexion" onClick={() => rememberSpace("employee")} className="text-slate-900 font-medium hover:underline" data-testid="to-employee-space-link">Ouvrir l'Espace Salarié</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
